<script>
import moment from "moment";
import axios from '@/utils/axios';
import "daterangepicker/daterangepicker.js";
import "daterangepicker/daterangepicker.css";
import DateRangePicker from "daterangepicker";
import Vue3ChartJs from "@j-t-mcc/vue3-chartjs";
import { clientAdmin } from "@/utils/auth";

export default {
  inject: ['setLoading'],
  components: { Vue3ChartJs },
  data() {
    return {
      errors: {},
      title: "Reports",
      text: "Inventory",
      clientAdmin: clientAdmin(),
      text1: "Stock In Report",
      app_currency: '',
      previewImage: '',
      filter: {
        from_date: '',
        to_date: '',
        warehouse_id: '',
        vendor_id: '',
        item_id: '',
        movement_type: 'in',
      },
      summary: {},
      data: [],
      warehouses: [],
      vendors: [],
      items: [],
      pagination: {
        current_page: 1,
        per_page: 25,
        last_page: 1,
        total: 0,
        links: []
      },
    };
  },

  computed: {
    serialNo() {
      return (this.pagination.current_page - 1) * this.pagination.per_page;
    },

    // Source-wise breakdown from current page data
    sourceBreakdown() {
      const breakdown = {
        purchase_order: { count: 0, qty: 0, amount: 0, label: 'Purchase Order', icon: 'ti-basket', color: 'primary' },
        delivery_challan_return: { count: 0, qty: 0, amount: 0, label: 'DC Return', icon: 'ti-arrow-back-up', color: 'success' },
        manual: { count: 0, qty: 0, amount: 0, label: 'Manual Entry', icon: 'ti-pencil', color: 'warning' },
      };
      (this.data || []).forEach(row => {
        const type = row.reference_type || 'manual';
        if (breakdown[type]) {
          breakdown[type].count++;
          breakdown[type].qty += parseFloat(row.qty || 0);
          breakdown[type].amount += parseFloat(row.taxable_amount || 0);
        }
      });
      return Object.values(breakdown).filter(b => b.count > 0);
    },

    itemBarChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.item?.name || 'Unknown';
        const qty = parseFloat(row.qty || 0);
        map[name] = (map[name] || 0) + qty;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#4f46e5', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
      return {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: 'Qty Received',
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderRadius: 6,
            barThickness: 40,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { beginAtZero: true, grid: { color: '#f3f4f6' } },
            x: { grid: { display: false } }
          }
        }
      };
    },

    warehouseDoughnutChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.warehouse?.name || 'Unknown';
        const amt = parseFloat(row.taxable_amount || 0);
        map[name] = (map[name] || 0) + amt;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#4f46e5', '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6'];
      return {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderWidth: 2,
            hoverOffset: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle' } }
          },
          cutout: '65%',
        }
      };
    },

    vendorDoughnutChart() {
      const map = {};
      (this.data || []).forEach(row => {
        const name = row.vendor?.name || 'Unknown';
        const amt = parseFloat(row.taxable_amount || 0);
        map[name] = (map[name] || 0) + amt;
      });
      const labels = Object.keys(map);
      const values = Object.values(map);
      const colors = ['#10b981', '#f59e0b', '#4f46e5', '#ef4444', '#06b6d4', '#8b5cf6'];
      return {
        type: 'doughnut',
        data: {
          labels,
          datasets: [{
            data: values,
            backgroundColor: labels.map((_, i) => colors[i % colors.length]),
            borderWidth: 2,
            hoverOffset: 6,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { position: 'bottom', labels: { padding: 15, usePointStyle: true, pointStyle: 'circle' } }
          },
          cutout: '65%',
        }
      };
    },
  },

  async mounted() {
    this.app_currency = await this.$appCurrency();
    this.getDropdowns();

    const start = moment().subtract(6, "days");
    const end = moment();

    this.filter.from_date = start.format("YYYY-MM-DD");
    this.filter.to_date = end.format("YYYY-MM-DD");

    new DateRangePicker(
    this.$refs.dateRangeInput,
    {
        startDate: start,
        endDate: end,
        autoUpdateInput: false,
        locale: { format: "DD/MM/YYYY", separator: " - ", cancelLabel: "Clear" },
        ranges: {
          Today: [moment(), moment()],
          Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
          "Last 7 Days": [moment().subtract(6, "days"), moment()],
          "Last 30 Days": [moment().subtract(29, "days"), moment()],
          "This Month": [moment().startOf("month"), moment().endOf("month")],
          "Last Month": [
            moment().subtract(1, "month").startOf("month"),
            moment().subtract(1, "month").endOf("month"),
          ],
        }
      },
        (start, end) => {
        this.filter.from_date = start.format("YYYY-MM-DD");
        this.filter.to_date = end.format("YYYY-MM-DD");
        this.$refs.dateRangeInput.value = start.format("DD/MM/YYYY") + " - " + end.format("DD/MM/YYYY");
        this.filterReport();
        }
    );

    this.filterReport();
  },

  methods: {

    viewInvoiceImage(receipt) {
      this.previewImage = receipt.document_url;
      this.$openModal('invoice_image_modal');
    },

    // Source type helpers
    sourceLabel(row) {
      const map = {
        purchase_order: 'Purchase Order',
        delivery_challan_return: 'DC Return',
        manual: 'Manual',
      };
      return map[row.reference_type] || 'Other';
    },

    sourceBadgeClass(row) {
      const map = {
        purchase_order: 'bg-primary-subtle text-primary',
        delivery_challan_return: 'bg-success-subtle text-success',
        manual: 'bg-warning-subtle text-warning',
      };
      return map[row.reference_type] || 'bg-secondary-subtle text-secondary';
    },

    sourceIcon(row) {
      const map = {
        purchase_order: 'ti ti-basket',
        delivery_challan_return: 'ti ti-arrow-back-up',
        manual: 'ti ti-pencil',
      };
      return map[row.reference_type] || 'ti ti-point';
    },

    rowBgClass(row) {
      const map = {
        delivery_challan_return: 'table-row-return',
        manual: 'table-row-manual',
      };
      return map[row.reference_type] || '';
    },

    referenceDisplay(row) {
      if (!row.reference) return '-';
      // Shorten long manual references
      if (row.reference_type === 'manual') {
        return row.reference.length > 20 ? row.reference.substring(0, 20) + '...' : row.reference;
      }
      return row.reference;
    },

    // From/Recipient — for PO show vendor, for DC Return show engineer/work order
    fromName(row) {
      if (row.reference_type === 'purchase_order') {
        return row.vendor?.name || '-';
      }
      if (row.reference_type === 'delivery_challan_return') {
        // recipient info
        const typeMap = {
          'App\\Models\\Engineer': 'Engineer',
          'App\\Models\\WorkOrder': 'Work Order',
          'App\\Models\\Customer': 'Customer',
          'App\\Models\\SalesExecutive': 'Sales Executive',
          'App\\Models\\InternalConsumption': 'Internal Consumption',
        };
        const typeLabel = typeMap[row.recipient_type] || '';
        return typeLabel || '-';
      }
      return row.vendor?.name || '-';
    },

    fromSubtext(row) {
      if (row.reference_type === 'purchase_order') {
        return 'Vendor';
      }
      if (row.reference_type === 'delivery_challan_return') {
        return 'Returned from';
      }
      if (row.reference_type === 'manual') {
        return row.vendor?.name ? 'Vendor' : '';
      }
      return '';
    },

async exportCSV() {
      this.setLoading(true);
      try {
        const payload = { export: 'csv' };
        if (this.filter.from_date) payload.date_from = this.filter.from_date;
        if (this.filter.to_date) payload.date_to = this.filter.to_date;
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.vendor_id) payload.vendor_id = this.filter.vendor_id;
        if (this.filter.item_id) payload.item_id = this.filter.item_id;
payload.movement_type = this.filter.movement_type;
        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/stock-in?${query_string}`, {
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `stock-in-report-${this.filter.from_date}-to-${this.filter.to_date}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } catch (error) {
        this.$swalToast('Failed to export CSV', 'error');
      } finally {
        this.setLoading(false);
      }
    },

    setDateRange(start, end) {
      this.filter.from_date = start.format("YYYY-MM-DD");
      this.filter.to_date = end.format("YYYY-MM-DD");
    },

    async filterReport(page = 1) {
      this.errors = {};
      this.setLoading(true);
      try {
        const payload = {
          page,
          date_from: this.filter.from_date,
          date_to: this.filter.to_date,
        };
        if (this.filter.warehouse_id) payload.warehouse_id = this.filter.warehouse_id;
        if (this.filter.vendor_id) payload.vendor_id = this.filter.vendor_id;
        if (this.filter.item_id) payload.item_id = this.filter.item_id;
        payload.movement_type = this.filter.movement_type;

        const query_string = new URLSearchParams(payload).toString();
        const response = await axios.get(`/reports/stock-in?${query_string}`);
        this.summary = response.data.data.summary || {};
        this.data = response.data.data.rows.data;
        this.pagination = {
          current_page: response.data.data.rows.current_page,
          per_page: response.data.data.rows.per_page,
          last_page: response.data.data.rows.last_page,
          total: response.data.data.rows.total,
          links: response.data.data.rows.links
        };
      } catch (error) {
        if (error.type == 'validation') {
          this.errors = error.errors;
        } else {
          this.$swalToast(error.message, 'error');
        }
      } finally {
        this.setLoading(false);
      }
    },

    async getDropdowns() {
      try {
        const [warehouseRes, vendorRes, itemRes] = await Promise.all([
          axios.get('/inventory/warehouses/dropdown'),
          axios.get('/inventory/vendors/dropdown'),
          axios.get('/items/dropdown'),
        ]);
        this.warehouses = warehouseRes.data.data;
        this.vendors = vendorRes.data.data;
        this.items = itemRes.data.data;
      } catch (error) {
        console.log('Dropdown load error:', error.message);
      }
    },

    changePage(url) {
      if (!url) return;
      const page = new URL(url).searchParams.get("page");
      this.filterReport(page);
    },

    hasActiveFilter() {
      return this.filter.warehouse_id || this.filter.vendor_id || this.filter.item_id || this.filter.from_date || this.filter.to_date;
    },

    clearFilter() {
      this.filter.warehouse_id = '';
      this.filter.vendor_id = '';
      this.filter.item_id = '';
      this.filter.from_date = '';
      this.filter.to_date = '';
      this.$refs.dateRangeInput.value = '';
      this.filterReport();
      this.filter.movement_type = 'in';
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-';
      const d = new Date(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        + ', ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    },

    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
    },

    formatQty(val) {
      const num = parseFloat(val || 0);
      return num % 1 === 0 ? num.toFixed(0) : num.toFixed(2);
    },

    changePctClass(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return 'text-success';
      if (num < 0) return 'text-danger';
      return 'text-muted';
    },

    changePctIcon(val) {
      const num = parseFloat(val || 0);
      if (num > 0) return 'ti ti-trending-up';
      if (num < 0) return 'ti ti-trending-down';
      return 'ti ti-minus';
    },
  }
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <button class="btn btn-outline-primary d-flex align-items-center" @click="exportCSV" :disabled="!data || !data.length">
            <i class="ti ti-file-type-csv me-2"></i>Export CSV
          </button>
        </div>
      </div>

      <!-- Summary Cards -->
      <div class="row g-3 mb-3" v-if="summary && summary.total_receipts">
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Receipts</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.total_receipts }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Qty</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatQty(summary.total_qty) }}</span>
              <span v-if="summary.qty_change_pct != null" :class="changePctClass(summary.qty_change_pct)" style="font-size:11px;">
                <i :class="changePctIcon(summary.qty_change_pct)" style="font-size:12px;"></i> {{ Math.abs(summary.qty_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Value</span>
              <span class="fw-bold text-success" style="font-size:20px;">{{ formatCurrency(summary.total_value) }}</span>
              <span v-if="summary.value_change_pct != null" :class="changePctClass(summary.value_change_pct)" style="font-size:11px;">
                <i :class="changePctIcon(summary.value_change_pct)" style="font-size:12px;"></i> {{ Math.abs(summary.value_change_pct) }}%
              </span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Total Tax</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.total_tax) }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Unique Items</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ summary.unique_items }}</span>
            </div>
          </div>
        </div>
        <div class="col-md-2 col-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3 text-center">
              <span class="text-muted d-block" style="font-size:11px;">Avg Rate/Unit</span>
              <span class="fw-bold text-dark" style="font-size:20px;">{{ formatCurrency(summary.avg_rate_per_unit) }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Source-wise Breakdown Cards -->
      <div class="row g-3 mb-3" v-if="sourceBreakdown.length > 1">
        <div class="col" v-for="src in sourceBreakdown" :key="src.label">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-body p-3">
              <div class="d-flex align-items-center justify-content-between mb-1">
                <span class="d-flex align-items-center">
                  <i class="ti me-2" :class="[src.icon, 'text-' + src.color]" style="font-size:18px;"></i>
                  <span class="fw-semibold" style="font-size:13px;">{{ src.label }}</span>
                </span>
                <span class="badge rounded-pill" :class="'bg-' + src.color + '-subtle text-' + src.color">{{ src.count }}</span>
              </div>
              <div class="d-flex align-items-center justify-content-between">
                <small class="text-muted">{{ formatQty(src.qty) }} qty</small>
                <small class="fw-semibold">{{ formatCurrency(src.amount) }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="row g-3 mb-3" v-if="data && data.length">
        <div class="col-md-6">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-chart-bar me-1 text-primary"></i>Item-wise Qty Received</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="itemBarChart" :key="'bar-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-building-warehouse me-1 text-primary"></i>By Warehouse</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="warehouseDoughnutChart" :key="'wh-'+data.length" />
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-0 border-0 shadow-sm">
            <div class="card-header border-0 pb-0">
              <h6 class="fw-bold mb-0"><i class="ti ti-building-store me-1 text-primary"></i>By Vendor</h6>
            </div>
            <div class="card-body" style="height:280px;">
              <vue3-chart-js v-bind="vendorDoughnutChart" :key="'vn-'+data.length" />
            </div>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="card border-0">
        <div class="card-body d-flex align-items-center justify-content-center flex-wrap pb-1">
          <div class="d-flex align-items-center flex-wrap mb-1 gap-2">
            <div class="me-3" v-if="hasActiveFilter()">
              <a href="javascript:void(0);" class="text-danger" @click="clearFilter()"><i class="ti ti-filter-x"></i></a>
            </div>
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input type="text" class="form-control date-range bookingrange" ref="dateRangeInput" placeholder="dd/mm/yyyy - dd/mm/yyyy">
                <span class="input-icon-addon"><i class="ti ti-chevron-down"></i></span>
              </div>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.warehouse_id"
                :options="warehouses"
                label="name"
                :reduce="w => w.id"
                placeholder="Warehouse"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.vendor_id"
                :options="vendors"
                label="name"
                :reduce="v => v.id"
                placeholder="Vendor"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
              <vue-select
                v-model="filter.item_id"
                :options="items"
                label="name"
                :reduce="i => i.id"
                placeholder="Item"
                :clearable="true"
                @update:modelValue="filterReport()"
                style="min-width: 180px;"
              ></vue-select>
            </div>
            <div class="me-3">
  <div style="display:inline-flex; align-items:center; gap:8px; padding:6px 14px; border-radius:8px; cursor:pointer; transition:all .2s;"
    :style="filter.movement_type === 'scrap' ? 'background:#fef2f2; border:1.5px solid #fca5a5;' : 'background:#f0fdf4; border:1.5px solid #bbf7d0;'"
    @click="filter.movement_type = filter.movement_type === 'scrap' ? 'in' : 'scrap'; filterReport()">
    <i class="ti" 
      :class="filter.movement_type === 'scrap' ? 'ti-trash text-danger' : 'ti-package text-success'"
      style="font-size:16px;"></i>
    <span style="font-size:12px; font-weight:600;"
      :style="filter.movement_type === 'scrap' ? 'color:#ef4444;' : 'color:#16a34a;'">
      {{ filter.movement_type === 'scrap' ? 'Scrap Items' : 'Stock In' }}
    </span>
    <span style="width:18px; height:18px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:10px;"
      :style="filter.movement_type === 'scrap' ? 'background:#fee2e2; color:#ef4444;' : 'background:#dcfce7; color:#16a34a;'">
      <i class="ti" :class="filter.movement_type === 'scrap' ? 'ti-check' : 'ti-check'"></i>
    </span>
  </div>
</div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="row">
        <div class="col-lg-12">
          <div class="card-body card-table-wrapper">
            <div class="custom-datatable-filter table-responsive">
              <table class="card-table" v-if="data && data.length">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Date</th>
                    <th>Source</th>
                    <th>Item</th>
                    <th>Warehouse</th>
                    <th>From</th>
                    <th>Invoice</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Tax</th>
                    <th>Amount</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in data" :key="row.id" :class="rowBgClass(row)">
                    <td>{{ serialNo + index + 1 }}.</td>
                    <td>
                      <span>{{ formatDate(row.created_at) }}</span>
                      <small class="text-muted d-block" style="font-size:10px;">{{ formatDateTime(row.created_at).split(', ')[1] }}</small>
                    </td>
                    <td>
                      <span class="badge rounded-pill" :class="sourceBadgeClass(row)">
                        <i :class="sourceIcon(row)" class="me-1" style="font-size:11px;"></i>{{ sourceLabel(row) }}
                      </span>
                      <small class="d-block text-muted mt-1" style="font-size:10px;" :title="row.reference">
                        {{ referenceDisplay(row) }}
                      </small>
                    </td>
                    <td>
                      <strong class="text-dark">{{ row.item?.name }}</strong>
                      <small class="text-muted d-block" style="font-size:11px;">{{ row.item?.code }}</small>
                    </td>
                    <td>
                      <span class="badge bg-light text-primary border rounded-pill">{{ row.warehouse?.name || '-' }}</span>
                    </td>
                    <td>
                      <span>{{ fromName(row) }}</span>
                      <small v-if="fromSubtext(row)" class="text-muted d-block" style="font-size:10px;">{{ fromSubtext(row) }}</small>
                    </td>
                    <td>
                      <span>{{ row.receipt?.invoice_number || '-' }}</span>
                      <a v-if="row.receipt?.invoice_image" href="javascript:void(0);" 
                        class="ms-2 text-primary" 
                        @click="viewInvoiceImage(row.receipt)" 
                        title="View Invoice Image">
                        <i class="ti ti-photo"></i>
                      </a>
                    </td>
                    <td><strong>{{ formatQty(row.qty) }}</strong></td>
                    <td>{{ formatCurrency(row.rate) }}</td>
                    <td>
                      <span v-if="row.tax">{{ row.tax.name }}</span>
                      <span v-else class="text-muted">-</span>
                      <small v-if="row.tax" class="text-muted d-block" style="font-size:10px;">{{ formatCurrency(row.tax_amount) }}</small>
                    </td>
                    <td><strong>{{ formatCurrency(row.taxable_amount) }}</strong></td>
                    <td>
                      <small class="text-muted">{{ row.created_by?.name || '-' }}</small>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="p-3 text-center text-muted">No records found.</div>

              <!-- Pagination -->
              <nav v-if="pagination.links && pagination.links.length > 3" class="m-3">
                <ul class="pagination pagination-sm">
                  <li v-for="(link, index) in pagination.links" :key="index"
                    :class="['page-item', { active: link.active, disabled: !link.url }]">
                    <a class="page-link" href="javascript:void(0)" v-html="link.label" @click="changePage(link.url)"></a>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>

  <!-- Image Preview Modal -->
  <div class="modal fade" id="invoice_image_modal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content" style="border:none;border-radius:12px;overflow:hidden;">
        <div class="modal-header border-0 pb-0">
          <h5 class="modal-title fw-bold">Invoice Image</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>
        <div class="modal-body text-center p-3">
          <img :src="previewImage" alt="Invoice" style="max-width:100%;max-height:70vh;border-radius:8px;" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.table-row-return {
  background-color: rgba(16, 185, 129, 0.04) !important;
}
.table-row-manual {
  background-color: rgba(245, 158, 11, 0.04) !important;
}
.table-responsive {
  overflow-y: auto;
}
</style>
